import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { platformTenants, platformCreateTenant } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { RequirePlatform } from "./guards";
import { Shell, Card, H1, Field, Btn, Label, Input } from "./ui";

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48);

function Console() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [form, setForm] = useState({ name: "", slug: "", contact_email: "", website_url: "", phone: "", primary: "#00D26A", secondary: "#161616", accent: "#ffffff" });
  const [inviteUrl, setInviteUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => platformTenants().then(setTenants).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v, ...(k === "name" && !f._slugTouched ? { slug: slugify(v) } : {}) }));

  const create = async (e) => {
    e.preventDefault();
    setBusy(true); setInviteUrl("");
    try {
      const r = await platformCreateTenant(form);
      toast.success(`Tenant "${r.name}" created`);
      setInviteUrl(r.owner_invite_url);
      setForm({ name: "", slug: "", contact_email: "", website_url: "", phone: "", primary: "#00D26A", secondary: "#161616", accent: "#ffffff" });
      load();
    } catch (er) {
      toast.error(er?.response?.data?.detail || "Could not create tenant");
    } finally { setBusy(false); }
  };

  return (
    <Shell>
      <div className="flex justify-between items-center">
        <div>
          <div className="text-[#00D26A] text-xs tracking-[0.3em] uppercase mb-1">Platform Console</div>
          <H1>Tenants</H1>
        </div>
        <div className="flex gap-4 text-sm items-center">
          <span className="text-white/40">{user?.email}</span>
          <Link to="/platform/billing" className="text-white/60 hover:text-[#00D26A]" data-testid="nav-platform-billing">Billing &amp; Plans</Link>
          <Link to="/platform/analytics" className="text-white/60 hover:text-[#00D26A]" data-testid="nav-platform-analytics">Analytics</Link>
          <Link to="/account" className="text-white/60 hover:text-[#00D26A]">Account</Link>
          <button onClick={async () => { await logout(); nav("/login"); }} className="text-white/60 hover:text-red-400">Sign out</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2">
          <Card>
            <h2 className="font-semibold mb-4">All tenants ({tenants.length})</h2>
            <div className="space-y-2" data-testid="tenant-list">
              {tenants.map((t) => (
                <div key={t.slug} className="flex justify-between items-center bg-[#0a0a0a] border border-white/10 rounded px-4 py-3" data-testid={`tenant-row-${t.slug}`}>
                  <div><div className="font-semibold">{t.name}</div><div className="text-xs text-white/40">{t.slug} · {t.members} member(s)</div></div>
                  <div className="text-xs text-white/50">{(t.admin_emails || [])[0]}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <Card>
          <h2 className="font-semibold mb-4">Create tenant</h2>
          <form onSubmit={create} data-testid="create-tenant-form">
            <Field label="Company name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="tenant-name" required />
            <Field label="Slug" value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: slugify(e.target.value), _slugTouched: true }))} data-testid="tenant-slug" required />
            <Field label="Owner contact email" type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="tenant-email" required />
            <Field label="Website URL" value={form.website_url} onChange={(e) => set("website_url", e.target.value)} data-testid="tenant-website" />
            <Field label="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="tenant-phone" />
            <div className="grid grid-cols-3 gap-2 mb-3">
              {["primary", "secondary", "accent"].map((k) => (
                <div key={k}><Label>{k}</Label><Input type="color" value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`tenant-${k}`} className="h-9 p-1" /></div>
              ))}
            </div>
            <Btn type="submit" disabled={busy} data-testid="create-tenant-submit" className="w-full">{busy ? "Creating…" : "Create tenant + invite owner"}</Btn>
          </form>
          {inviteUrl && (
            <div className="mt-4 text-xs bg-[#0a0a0a] border border-[#00D26A]/40 rounded p-3" data-testid="owner-invite-url">
              <div className="text-white/50 mb-1">Owner invite link (also emailed):</div>
              <div className="break-all text-[#00D26A]">{inviteUrl}</div>
            </div>
          )}
        </Card>
      </div>
    </Shell>
  );
}

export default function PlatformConsole() { return <RequirePlatform><Console /></RequirePlatform>; }
